/**
 * FormBuilder field config for meeting room selection.
 * Generic — name and label must be provided by consumer.
 * Uses the meeting rooms API for async options.
 */
import { useCallback, useMemo } from 'react';
import type { SelectOption } from '@kula-ai/components';

interface UseMeetingRoomFieldConfigProps {
  name: string;
  label: string;
  condition?: Record<string, unknown>;
  placeholder?: string;
  isMulti?: boolean;
  /** Preloaded room options for cases where rooms are fetched externally (e.g., via trigger+poll). */
  roomOptions?: SelectOption[];
}

export function useMeetingRoomFieldConfig(
  props: UseMeetingRoomFieldConfigProps
) {
  const options = useMemo(() => {
    return props.roomOptions ?? [];
  }, [props.roomOptions]);

  const filterOptions = useCallback(
    (query: string) => {
      if (!query) return options;
      const q = query.toLowerCase();
      return options.filter((o) => o.label.toLowerCase().includes(q));
    },
    [options]
  );

  const fieldConfig = useMemo(
    () => ({
      type: 'select' as const,
      name: props.name,
      label: props.label,
      options,
      condition: props.condition,
      fieldProps: {
        searchable: true,
        mode: (props.isMulti ? 'multiple' : 'single') as 'single' | 'multiple',
        placeholder: props.placeholder ?? 'Select meeting room',
        menuPortalTarget: null,
        filterOptions,
      },
    }),
    [
      props.name,
      props.label,
      props.condition,
      props.isMulti,
      props.placeholder,
      options,
      filterOptions,
    ]
  );

  return { fieldConfig, options };
}
